import { observer } from "mobx-react";
import { useState, useEffect } from "react";
import { useStore } from "../../../../app/data/root.store";
import { colors } from '../../../../../common/styles/theme.styles';
import { VaultModalHeader, VaultModalContent, VaultModalFooter, VaultActionButton, VaultWarningMessage } from './SharedModalComponents';
import { BaseModal } from "../../../../../common/components/BaseModal/BaseModal";
import {
    PeriodEditorContainer,
    QuarterOption,
    QuarterSelector,
    YearArrow,
    YearDisplay,
    YearSelector
} from "../../VaultMetadataPanel/MetadataLabelsSection/PeriodTagCategory/PeriodTagCategory.styles";

const BulkPeriodEditor = ({ selectedPeriods, onPeriodsChange }) => {
    const [selectedYear, setSelectedYear] = useState(2025);
    const [selectedPeriodsSet, setSelectedPeriodsSet] = useState(new Set());

    useEffect(() => {
        const periodsSet = new Set(selectedPeriods);
        setSelectedPeriodsSet(periodsSet);

        const yearMatch = selectedPeriods.find(p => p.match(/\d{4}/));
        if (yearMatch) {
            const year = yearMatch.match(/\d{4}/)[0];
            setSelectedYear(parseInt(year));
        }
    }, [selectedPeriods]);

    const handlePeriodToggle = (period) => {
        setSelectedPeriodsSet(prev => {
            const newSelection = new Set();

            if (period === selectedYear.toString()) {
                if (!prev.has(period)) {
                    newSelection.add(period);
                }
            } else {
                if (!prev.has(period)) {
                    newSelection.add(period);
                }
            }

            onPeriodsChange(Array.from(newSelection));
            return newSelection;
        });
    };

    const isYearSelected = () => {
        return selectedPeriodsSet.has(selectedYear.toString());
    };

    const isQuarterSelected = (quarter) => {
        return selectedPeriodsSet.has(`${quarter}-${selectedYear}`);
    };

    return (
        <PeriodEditorContainer>
            <YearSelector>
                <YearArrow isLeft onClick={() => setSelectedYear(prev => prev - 1)}>‹</YearArrow>
                <YearDisplay>{selectedYear}</YearDisplay>
                <YearArrow isRight onClick={() => setSelectedYear(prev => prev + 1)}>›</YearArrow>
            </YearSelector>
            <QuarterSelector>
                <QuarterOption
                    isSelected={isYearSelected()}
                    onClick={() => handlePeriodToggle(selectedYear.toString())}
                >
                    All
                </QuarterOption>
                {['Q1', 'Q2', 'Q3', 'Q4'].map(quarter => (
                    <QuarterOption
                        key={quarter}
                        isSelected={isQuarterSelected(quarter)}
                        onClick={() => handlePeriodToggle(`${quarter}-${selectedYear}`)}
                    >
                        {quarter}
                    </QuarterOption>
                ))}
            </QuarterSelector>
        </PeriodEditorContainer>
    );
};

export const PeriodEditModal = observer(({ onClose, onSave }) => {
    const { vaultStore } = useStore();
    const [selectedPeriods, setSelectedPeriods] = useState([]);

    useEffect(() => {
        if (vaultStore.selectedFilesForBulkEdit.length > 0) {
            const commonPeriods = vaultStore.commonPeriodsAcrossSelectedFiles;
            setSelectedPeriods(commonPeriods);
        }
    }, [vaultStore.selectedFilesForBulkEdit, vaultStore.commonPeriodsAcrossSelectedFiles]);

    const handleSave = () => {
        onSave(selectedPeriods);
    };

    const getModalTitle = () => {
        const count = vaultStore.selectedFilesForBulkEdit.length;
        return `Edit selected item${count === 1 ? '' : 's'} (${count})`;
    };

    return (
        <BaseModal width="600px" borderRadius={12} onBackDropClick={onClose}>
            <VaultModalHeader onClose={onClose}>
                {getModalTitle()}
            </VaultModalHeader>

            <VaultModalContent>
                <BulkPeriodEditor
                    selectedPeriods={selectedPeriods}
                    onPeriodsChange={setSelectedPeriods}
                />
                
                <VaultWarningMessage>
                    This action will replace all existing periods with the selected periods for all selected files.
                </VaultWarningMessage>
            </VaultModalContent>

            <VaultModalFooter>
                <VaultActionButton
                    onClick={handleSave}
                    backgroundColor={colors.darkGray1}
                    whiteText
                >
                    Save
                </VaultActionButton>
            </VaultModalFooter>
        </BaseModal>
    );
}); 